import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import NavBar from "../../components/common/NavBar";
import MapComponent from "../../components/common/Map";
import AreaPicker from "../../components/areas/AreaPicker";
import type { RowExtras, RowState } from "../../components/areas/AreaPicker";
import DraftDataBadge from "../../components/data/DraftDataBadge";
import DataStateNotice from "../../components/data/DataStateNotice";
import CountryFilter from "../../components/areas/CountryFilter";
import { describeRequestError } from "../../services/http";
import { useAreaShapes } from "../../geo/areaShapes";
import type { AreaCell } from "../../geo/areaShapes";
import { userMapsApi } from "../../services/userMapsApi";
import { projectsApi, Project } from "../../services/projectsApi";
import { COUNTRIES, DEFAULT_COUNTRY, areaCountry } from "../../config/countries";
import type { CountryCode } from "../../config/countries";
import { PRODUCT_LABELS, capability, geoTypeLabel, supportStatus } from "../../config/products";
import { canUse } from "../../config/catalog";
import { useCountryFilter } from "../../hooks/useCountryFilter";
import { partitionBy } from "../../utils/partitionAreas";
import { pluralize } from "../../utils/plural";
import {
  addAreas,
  odmProjectSelectable,
  odmScopeFromState,
  removeAreas,
  selectionAreaIds,
  selectionCountry,
  selectionGeoType,
} from "../../utils/odmSelection";
import type { SelectedArea } from "../../utils/odmSelection";

interface SavedArea {
  id: string;
  name: string;
  description: string;
  type: string;
  ids?: string[];
  geometry?: string;
  country_code?: string | null;
}

const SELECTED_COLOR = "blue";

const OdmAreaSelectPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // The country filter: All by default, or the country of the ?country= in the URL.
  const { country: filter, setCountry: setFilter } = useCountryFilter();
  const [savedAreas, setSavedAreas] = useState<SavedArea[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // The one selection: saved areas of one country and one area type, in the order they were picked. Coming
  // back from the map restores it.
  const [selection, setSelection] = useState<SelectedArea[]>(() => odmScopeFromState(location.state)?.selection ?? []);

  // The shapes of the selection, for the map and for the aggregation page.
  const [shownCells, setShownCells] = useState<AreaCell[]>([]);
  const [viewState, setViewState] = useState<any>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const [response, projs] = await Promise.all([
          userMapsApi.loadGeometries(),
          projectsApi.list().catch(() => [] as Project[]),
        ]);
        if (cancelled) return;
        setSavedAreas([...response.maps].sort((a, b) => a.name.localeCompare(b.name)));
        setProjects(projs);
      } catch (err) {
        console.error("Failed to load saved areas:", err);
        if (!cancelled) setError(describeRequestError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Every saved area is listed (country, then type); the filter narrows the list to one country.
  const listed = useMemo(
    () => (filter ? savedAreas.filter((a) => areaCountry(a) === filter) : savedAreas),
    [savedAreas, filter],
  );
  const usableListed = useMemo(() => listed.filter((a) => canUse("odm", a, []).supported), [listed]);
  const areaById = useMemo(() => new Map(savedAreas.map((a) => [a.id, a])), [savedAreas]);

  // A project shows the members the filter lets through; a project with none is hidden.
  const visibleProjects = useMemo(
    () =>
      projects
        .map((project) => ({
          project,
          members: project.areaIds
            .map((id) => areaById.get(id))
            .filter((a): a is SavedArea => a !== undefined && (!filter || areaCountry(a) === filter)),
        }))
        .filter((entry) => entry.members.length > 0),
    [projects, areaById, filter],
  );

  // Order: Your projects, Selected areas, Other areas. The selected areas stay on show whatever the filter says,
  // so a pick is never hidden behind another country's chip.
  const selectedIds = useMemo(() => new Set(selection.map((s) => s.id)), [selection]);
  const selectedAreas = useMemo(() => savedAreas.filter((a) => selectedIds.has(a.id)), [savedAreas, selectedIds]);
  const { rest: otherAreas } = useMemo(() => partitionBy(listed, (a) => selectedIds.has(a.id)), [listed, selectedIds]);

  const geoType = selectionGeoType(selection);
  // The country of the selection once there is one, else the filter; with neither the list covers every country.
  const country: CountryCode | null = selectionCountry(selection) ?? filter;
  const selectedCountry = selectionCountry(selection);

  const toggleArea = useCallback(
    (area: SavedArea) =>
      setSelection((current) =>
        current.some((s) => s.id === area.id) ? removeAreas(current, [area.id]) : addAreas(current, [area]),
      ),
    [],
  );

  const toggleProject = useCallback((members: SavedArea[]) => {
    setSelection((current) => {
      const result = odmProjectSelectable(members, current);
      if (!result.ok) return current;
      const allIn = result.areas.every((a) => current.some((s) => s.id === a.id));
      return allIn ? removeAreas(current, result.areas.map((a) => a.id)) : addAreas(current, result.areas);
    });
  }, []);

  // The shapes of the selection (one request: every area has the same type). A newer selection, or "Try again",
  // replaces the request; the shapes already on the map stay until the new ones arrive.
  const selectionAreas = useMemo(
    () =>
      selection.length > 0 && geoType && selectedCountry
        ? // The saved code, not the registry's: the catalogue's own code ('no') is what marks an area made on its maps.
          [{ id: "odm-selection", type: geoType, ids: selectionAreaIds(selection), country_code: selection[0].country_code ?? selectedCountry }]
        : [],
    [selection, geoType, selectedCountry],
  );
  const shapes = useAreaShapes(selectionAreas);
  const wantsShapes = selectionAreas.length > 0;
  const noCountry = selection.length > 0 && Boolean(geoType) && !selectedCountry;
  const isLoadingGeometries = wantsShapes && shapes.status === "loading";
  const geometriesLoaded = wantsShapes && shapes.status === "ready";
  const geometryError = noCountry
    ? "The app can't tell which country these areas are in, so their shapes can't be loaded."
    : shapes.status === "failed"
      ? describeRequestError(shapes.error)
      : null;

  // Adopt the shapes the moment they are ready (in the same render, so Continue never hands on a stale list).
  if (shapes.status === "ready" && shownCells !== shapes.cells) setShownCells(shapes.cells);
  const mapGeometries = useMemo(() => shownCells.map((c) => ({ ...c, color: SELECTED_COLOR })), [shownCells]);

  // Frame the map on the chosen areas; with none, fall back to the country's default view.
  useEffect(() => {
    if (mapGeometries.length === 0) {
      setViewState(null);
      return;
    }
    const t = setTimeout(() => setViewState({ ...shapes.view, pitch: 0, bearing: 0 }), 120);
    return () => clearTimeout(t);
    // Re-frame only when the shapes on the map change, not when the view object does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapGeometries]);

  const canContinue = selection.length > 0 && geometriesLoaded && !isLoadingGeometries && !geometryError;

  const handleContinue = () => {
    if (!canContinue || !geoType) return;
    navigate("/odm", {
      state: { selection, geoType, countryCode: selectedCountry, geometries: mapGeometries, pickerSearch: location.search },
    });
  };

  // The note under the heading says what Origin-Destination supports in the filtered (or selected) country, from
  // the registry; where nothing is supported yet it gives the registry's reason instead. With every country
  // shown and nothing selected there is no single country to speak of, so no note.
  let odmNote: string | null = null;
  if (country) {
    const odmGeoTypes = (capability(country, "odm")?.geo ?? []).filter((g) => !g.blocked).map((g) => g.geoType);
    const noOdm = supportStatus(country, "odm", "model_grid");
    if (odmGeoTypes.length > 0) {
      odmNote = `${PRODUCT_LABELS.odm} in ${COUNTRIES[country].label} supports: ${odmGeoTypes
        .map((t) => geoTypeLabel(t, country))
        .join(", ")}`;
    } else if (!noOdm.ok) {
      odmNote = noOdm.reason;
    }
  }

  // Where the map starts: the selection's country, else the filter, else the country of the first area listed.
  const mapCountry: CountryCode = country ?? (listed[0] ? areaCountry(listed[0]) : null) ?? DEFAULT_COUNTRY;

  const renderRowExtras = (area: SavedArea, state: RowState): RowExtras => ({
    badge: state.ok ? <DraftDataBadge country={areaCountry(area)} product="odm" geoType={area.type} /> : null,
  });

  return (
    <div className="min-h-screen bg-base-200 flex flex-col">
      <NavBar title={PRODUCT_LABELS.odm} showBackButton={true} backTo="/" />

      <div className="flex flex-col items-center flex-grow p-4 pb-28">
        <div className="w-full max-w-6xl">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold">Select areas</h1>
            <p className="text-lg text-base-content/70 mt-2">
              Pick a project, or one or more areas of the same country and type. You choose the direction of the trips on the next screen.
            </p>
            <CountryFilter areas={savedAreas} value={filter} onChange={setFilter} className="mt-4" />
            {odmNote && <p className="text-sm text-ink-muted mt-2">{odmNote}</p>}
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="loading loading-spinner loading-lg"></div>
              <span className="ml-4 text-lg">Loading saved areas...</span>
            </div>
          ) : error ? (
            <div className="alert alert-error max-w-2xl mx-auto">
              <span>{error}</span>
            </div>
          ) : savedAreas.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-lg text-base-content/70">No saved areas yet</p>
              <p className="text-sm text-base-content/50 mt-2">Create and save an area to see it here.</p>
              <div className="mt-6">
                <button className="btn btn-primary" onClick={() => navigate("/?section=areas")}>
                  Create New Area
                </button>
              </div>
            </div>
          ) : filter && usableListed.length === 0 && selection.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-lg text-base-content/70">{`No saved areas you can use for ${PRODUCT_LABELS.odm} in ${COUNTRIES[filter].label}`}</p>
              <div className="mt-6">
                <button className="btn btn-primary" onClick={() => setFilter(null)}>
                  Show all countries
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="mb-8 h-72 w-full rounded-lg overflow-hidden border border-base-300 bg-base-100">
                <MapComponent
                  key={mapCountry}
                  initialView={COUNTRIES[mapCountry].defaultView}
                  viewState={viewState ?? undefined}
                  onViewStateChange={setViewState}
                  geometries={mapGeometries}
                  outlineError={shapes.status === "failed" ? { onRetry: shapes.retry } : undefined}
                  data={[]}
                  selectedPoints={[]}
                  onPointClick={() => {}}
                />
              </div>

              <AreaPicker
                product="odm"
                mode="multi"
                selection={selection}
                headingLevel={2}
                onChange={(area) => toggleArea(area)}
                onToggleProject={toggleProject}
                projectsTitle="Your projects"
                projects={visibleProjects.map(({ project, members }) => ({ id: project.id, name: project.name, members }))}
                groups={[
                  { id: "selected", title: "Selected areas", areas: selectedAreas },
                  { id: "other", title: "Other areas", areas: otherAreas },
                ]}
                renderRowExtras={renderRowExtras}
                showUnsupportedSelect
              />

              {geometryError ? (
                <div className="mx-auto w-full max-w-xl text-left">
                  <DataStateNotice
                    tone="error"
                    title="Couldn't load the areas"
                    body={geometryError}
                    action={{ label: "Try again", onClick: shapes.retry }}
                  />
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>

      <div className="sticky bottom-0 z-10 border-t border-base-300 bg-base-100">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-4 p-4">
          <p className="text-sm" aria-live="polite">
            {pluralize(selection.length, "area")} selected
            {isLoadingGeometries ? <span className="ml-2 text-ink-muted">Loading shapes...</span> : null}
          </p>
          <button className="btn btn-primary" onClick={handleContinue} disabled={!canContinue}>
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default OdmAreaSelectPage;
